import { Component, EventEmitter, OnDestroy, Output, ViewChild } from '@angular/core';
import { TpModalComponent } from 'app/components/tp-modal/tp-modal.component';
import { FormControl } from '@angular/forms';
import { RefDataService } from 'app/services/general/refData.service';
import { NotificationService } from 'app/services/notification.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
    AiCatCategory,
    PostAiCatToolAnalysisSubmit,
    ResponseAiCatToolAnalysisExtract,
    ResponseCatToolAnalysisResult
} from '../../models/cat-tool-analysis';
import { BaseResultModel } from 'app/models';
import { PostFile } from 'app/models/general/system-models/post-file';
import { FileAutoUpload } from 'app/components/drag-and-drop-file/drag-and-drop-file.component';

type AiStep = 'upload' | 'processing' | 'preview' | 'error';

@Component({
    selector: 'tp-cat-count-analyse-ai-modal',
    templateUrl: './cat-count-analyse-ai-modal.component.html',
    styleUrls: ['./cat-count-analyse-ai-modal.component.scss']
})
export class CatCountAnalyseAiModalComponent implements OnDestroy {
    @ViewChild('modal') modal: TpModalComponent;
    @Output() saved = new EventEmitter<ResponseCatToolAnalysisResult>();

    step: AiStep = 'upload';
    errorMessage = '';

    toolHint: FormControl = new FormControl('');
    uploadFiles: PostFile[] = [];
    autoUpload: Partial<FileAutoUpload> = { status: false };
    selectedFile: File | null = null;

    extract: ResponseAiCatToolAnalysisExtract | null = null;
    clientId: number | null = null;
    resourceId: number | null = null;
    catToolId: number | null = null;

    private obs$: Subject<void> = new Subject<void>();

    constructor(
        private _refDataService: RefDataService,
        private _notificationService: NotificationService
    ) {}

    ngOnDestroy(): void {
        this.obs$.next();
        this.obs$.complete();
    }

    show(clientId?: number, resourceId?: number, catToolId?: number): void {
        this.step = 'upload';
        this.errorMessage = '';
        this.selectedFile = null;
        this.uploadFiles = [];
        this.toolHint.setValue('');
        this.extract = null;
        this.clientId = clientId ?? null;
        this.resourceId = resourceId ?? null;
        this.catToolId = catToolId ?? null;
        this.modal.show();
    }

    hide(): void {
        // Cancel any in-flight extract/submit request so a late response cannot flip the state
        // back to 'preview' after the user dismissed the modal.
        this.obs$.next();
        this.modal.hide();
    }

    onFilesSelected(files: PostFile[]): void {
        if (!files || files.length === 0) {
            this.selectedFile = null;
            this.uploadFiles = [];
            return;
        }
        const picked = files[0];
        if (!this.allowedFormat(picked?.file?.name)) {
            this._notificationService.showError(
                'Only .xml, .csv, and .xlsx files are allowed.',
                'Warning');
            this.selectedFile = null;
            this.uploadFiles = [];
            return;
        }
        this.uploadFiles = [picked];
        this.selectedFile = picked.file ?? null;
    }

    private allowedFormat(filename?: string): boolean {
        if (!filename) return false;
        const parts = filename.split('.');
        const ext = parts[parts.length - 1]?.toLowerCase();
        return ext === 'xml' || ext === 'csv' || ext === 'xlsx';
    }

    analyzeWithAi(): void {
        if (!this.selectedFile) {
            this._notificationService.showError('Please choose a file first.', 'Warning');
            return;
        }

        const MAX_BYTES = 500 * 1024;
        if (this.selectedFile.size > MAX_BYTES) {
            this._notificationService.showError(
                'File is too large for AI analysis (max 500 KB). Please split the file or pick a preset schema from the dropdown.',
                'Warning');
            return;
        }

        const formData = new FormData();
        formData.append('File', this.selectedFile);
        if (this.toolHint.value) formData.append('ToolHint', this.toolHint.value);

        this.step = 'processing';
        this._refDataService.aiExtractCatToolAnalysis(formData)
            .pipe(takeUntil(this.obs$))
            .subscribe(
                (res: BaseResultModel<ResponseAiCatToolAnalysisExtract>) => {
                    if (res?.status && res.response) {
                        this.extract = res.response;
                        if (!this.extract.categories || this.extract.categories.length === 0) {
                            this.step = 'error';
                            this.errorMessage = res.message || 'The AI could not find any CAT categories in this file.';
                            return;
                        }
                        this.step = 'preview';
                    } else {
                        this.step = 'error';
                        this.errorMessage = res?.message || 'The AI could not process this file.';
                    }
                },
                () => {
                    this.step = 'error';
                    this.errorMessage = 'An error occurred while calling the AI service.';
                }
            );
    }

    confidenceClass(c?: number): string {
        const v = c ?? 0;
        if (v >= 0.8) return 'conf-high';
        if (v >= 0.5) return 'conf-mid';
        return 'conf-low';
    }

    confidenceLabel(c?: number): string {
        const v = c ?? 0;
        return `${Math.round(v * 100)}%`;
    }

    targetFieldLabel(f?: string): string {
        switch (f) {
            case 'Match101Perc':             return 'Perfect / 101%';
            case 'Match100Perc':             return '100% Match';
            case 'Match9599Perc':            return 'Fuzzy 95–99';
            case 'Match8594Perc':            return 'Fuzzy 85–94';
            case 'Match7584Perc':            return 'Fuzzy 75–84';
            case 'Match5074Perc':            return 'Fuzzy 50–74';
            case 'NoMatchPerc':              return 'No Match';
            case 'RepetitionsPerc':          return 'Repetitions';
            case 'CrossFileRepetitionsPerc': return 'Cross-File Repetitions';
            case 'ContextExact':             return 'Context Match';
            case 'XtranslatedPerc':          return 'X-Translated';
            default: return f ?? '';
        }
    }

    submitToPipeline(): void {
        if (!this.extract || !this.extract.categories || this.extract.categories.length === 0) return;

        const payload: PostAiCatToolAnalysisSubmit = {
            catToolId: this.catToolId ?? undefined,
            clientId: this.clientId ?? 0,
            resourceId: this.resourceId ?? 0,
            categories: this.extract.categories as AiCatCategory[]
        };

        this.step = 'processing';
        this._refDataService.aiSubmitCatToolAnalysis(payload)
            .pipe(takeUntil(this.obs$))
            .subscribe(
                (res: BaseResultModel<ResponseCatToolAnalysisResult>) => {
                    if (res?.status && res.response) {
                        // Hide before emit so the parent's next modal doesn't briefly stack on top of this one.
                        this.hide();
                        this.saved.emit(res.response);
                    } else {
                        this.step = 'error';
                        this.errorMessage = res?.message || 'Submission failed.';
                    }
                },
                () => {
                    this.step = 'error';
                    this.errorMessage = 'An error occurred while submitting the analysis.';
                }
            );
    }

    backToUpload(): void {
        this.step = 'upload';
        this.errorMessage = '';
    }
}
